/**
 * Fits a game iframe to the stage (from the game library dashboard's app.ts). The game lays itself out at its
 * natural width (max 960px), then the iframe is scaled uniformly so the whole page fits the stage.
 *
 * Loop safety: nothing inside the iframe is observed with a ResizeObserver (Chromium lays the nested frame out
 * during the parent's observer loop, which trips the loop limit). Instead the parent-side stage is observed,
 * the frame's own resize event is followed, and the content height is polled at a low rate.
 */
const GAME_BASE_W = 960;
let observer: ResizeObserver | null = null;
let raf = 0;
let poll = 0;
let cleanup: (() => void) | null = null;

export function stopFit() {
  observer?.disconnect(); observer = null;
  clearInterval(poll); cancelAnimationFrame(raf);
  cleanup?.(); cleanup = null;
}

export function fitIframe(iframe: HTMLIFrameElement, stage: HTMLElement) {
  stopFit();
  const doc = iframe.contentDocument, win = iframe.contentWindow;
  if (!doc?.documentElement || !win) { iframe.style.cssText = ""; return; }
  doc.documentElement.style.overflow = "hidden";
  let lastW = -1, lastH = -1, lastTf = "";
  const apply = () => {
    if (iframe.contentDocument !== doc) return;
    const sw = stage.clientWidth, sh = stage.clientHeight;
    if (!sw || !sh) return;
    const natW = Math.min(GAME_BASE_W, sw);
    if (natW !== lastW) { lastW = natW; iframe.style.width = `${natW}px`; }
    const natH = Math.max(doc.body?.scrollHeight ?? 0, doc.documentElement.scrollHeight, 1);
    if (Math.abs(natH - lastH) > 1) { lastH = natH; iframe.style.height = `${natH}px`; }
    const scale = Math.min(sw / natW, sh / lastH);
    const left = Math.max(0, (sw - natW * scale) / 2);
    const top = Math.max(0, (sh - lastH * scale) / 2);
    const tf = `${scale.toFixed(4)} ${left.toFixed(1)} ${top.toFixed(1)}`;
    if (tf !== lastTf) {
      lastTf = tf;
      iframe.style.transform = `scale(${scale.toFixed(4)})`;
      iframe.style.left = `${left.toFixed(1)}px`;
      iframe.style.top = `${top.toFixed(1)}px`;
    }
  };
  const schedule = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(apply); };
  apply();
  observer = new ResizeObserver(schedule);
  observer.observe(stage);
  win.addEventListener("resize", schedule);
  poll = window.setInterval(schedule, 500);
  cleanup = () => win.removeEventListener("resize", schedule);
}
